import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash, FileArrowDown, X, EnvelopeSimple, ArrowsClockwise, Stop, CreditCard, ChartBar, Users } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = [
  { code: "EUR", sym: "\u20ac" },
  { code: "GBP", sym: "\u00a3" },
  { code: "USD", sym: "$" },
];
const SYM = { EUR: "\u20ac", GBP: "\u00a3", USD: "$" };
const STATUS_STYLE = {
  draft: "badge-warn", sent: "badge-info", paid: "badge-pass", void: "badge-fail",
};

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const newInvLine = () => ({ _id: crypto.randomUUID(), description: "", qty: "1", unit_price: "" });

export default function Invoices() {
  const [data, setData] = useState({ items: [], count: 0, total_base: 0, outstanding_base: 0 });
  const [recurring, setRecurring] = useState({ items: [], count: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [aging, setAging] = useState(null);
  const [clients, setClients] = useState({ items: [] });
  const [directory, setDirectory] = useState({ items: [] });
  const [open, setOpen] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [showClients, setShowClients] = useState(false);
  const [chasing, setChasing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, rec, an, ag, cl, dir] = await Promise.all([
        api.get("/invoices"), api.get("/invoices/recurring"), api.get("/invoices/analytics"),
        api.get("/invoices/aging"), api.get("/invoices/clients"), api.get("/clients"),
      ]);
      setData(r.data); setRecurring(rec.data); setAnalytics(an.data); setAging(ag.data); setClients(cl.data); setDirectory(dir.data);
    } catch (e) { notifyError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Return from Stripe Checkout: confirm + mark the invoice paid
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("paid");
    if (!sid) return;
    (async () => {
      try {
        const r = await api.get(`/invoices/pay-status/${sid}`);
        if (r.data.payment_status === "paid") toast.success("Payment received — invoice marked paid");
      } catch (e) { console.debug("pay-status check failed", e); }
      window.history.replaceState({}, "", "/invoices");
      load();
    })();
  }, [load]);

  const setStatus = async (id, status) => { await api.put(`/invoices/${id}/status`, { status }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this invoice?")) return; await api.delete(`/invoices/${id}`); load(); };
  const makeRecurring = async (inv) => {
    if (!window.confirm(`Turn ${inv.number} into a monthly recurring invoice? A fresh draft will be raised every month.`)) return;
    try { await api.post("/invoices/recurring", { invoice_id: inv.invoice_id, interval: "monthly" }); load(); }
    catch (e) { notifyError(e); }
  };
  const stopRecurring = async (id) => { await api.delete(`/invoices/recurring/${id}`); load(); };
  const downloadPdf = async (inv) => {
    const r = await api.get(`/invoices/${inv.invoice_id}.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `${inv.number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const downloadStatement = async (c) => {
    const r = await api.get(`/invoices/statement.pdf?client_key=${encodeURIComponent(c.client_key)}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `statement-${(c.client_name || "client").replace(/\s+/g, "_")}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const emailStatement = async (c) => {
    try { const r = await api.post("/invoices/statement/email", { client_key: c.client_key }); toast.success(`Statement emailed (${r.data.to})`); }
    catch (e) { notifyError(e); }
  };
  const chaseOverdue = async () => {
    setChasing(true);
    try { const r = await api.post("/invoices/remind-overdue"); toast.success(`Reminders sent: ${r.data.sent} (${r.data.skipped} skipped)`); load(); }
    catch (e) { notifyError(e); } finally { setChasing(false); }
  };
  const activeRec = (recurring.items || []).filter((x) => x.active);
  const overdueCount = (aging?.buckets || []).filter((b) => b.key !== "current").reduce((s, b) => s + b.count, 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Sales</div>
          <h1 className="font-display text-4xl">Invoices</h1>
          <p className="text-white/60 text-sm mt-2">Raise customer invoices in EUR, GBP or USD. Every total is also converted to your EUR reporting base at the ECB rate on the issue date.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowClients(true)} data-testid="clients-btn" className="btn-ghost"><Users weight="bold" size={16} /> Clients</button>
          <button onClick={() => setOpen(true)} data-testid="new-invoice-btn" className="btn-gold"><Plus weight="bold" size={16} /> New invoice</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Tile label="Invoices" value={data.count} />
        <Tile label="Total invoiced (EUR base)" value={`\u20ac${money(data.total_base)}`} />
        <Tile label="Outstanding (EUR base)" value={`\u20ac${money(data.outstanding_base)}`} warn={data.outstanding_base > 0} />
      </div>

      {analytics && analytics.total_count > 0 && <InvoiceAnalytics a={analytics} aging={aging} />}

      {(clients.items || []).length > 0 && (
        <div className="glass rounded-xl p-6" data-testid="client-statements">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 eyebrow"><Users size={14} weight="duotone" color="#5B8DEF" /> Client statements</div>
            {overdueCount > 0 && (
              <button onClick={chaseOverdue} disabled={chasing} data-testid="chase-overdue-btn" className="btn-ghost text-xs text-[#FFB020]"><EnvelopeSimple size={14} /> {chasing ? "Chasing…" : `Chase overdue (${overdueCount})`}</button>
            )}
          </div>
          <div className="space-y-2">
            {clients.items.map((c) => (
              <div key={c.client_key} className="flex items-center justify-between rounded-lg bg-black/25 border border-white/5 p-3" data-testid={`client-${c.client_key}`}>
                <div className="text-sm">
                  <span className="text-white/85">{c.client_name || c.client_key}</span>
                  <span className="text-white/40 text-xs ml-2">{c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs">Outstanding <span className={`mono ${c.outstanding_base > 0 ? "text-[#FFB020]" : "text-white/50"}`}>{"\u20ac"}{money(c.outstanding_base)}</span></span>
                  {c.client_key.includes("@") && <button onClick={() => emailStatement(c)} data-testid={`statement-email-${c.client_key}`} className="btn-ghost text-xs"><EnvelopeSimple size={14} /> Email</button>}
                  <button onClick={() => downloadStatement(c)} data-testid={`statement-${c.client_key}`} className="btn-ghost text-xs"><FileArrowDown size={14} /> Statement</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass rounded-xl overflow-auto" data-testid="invoices-table">
        <table className="data">
          <thead><tr><th>Number</th><th>Client</th><th>Issued</th><th className="text-right">Total</th><th className="text-right">EUR base</th><th>Status</th><th /></tr></thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr><td colSpan="7" className="text-center text-white/40 py-10">No invoices yet. Create your first invoice.</td></tr>
            ) : data.items.map((inv) => (
              <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                <td className="mono text-xs">{inv.number}{inv.recurring_id ? <ArrowsClockwise size={11} className="inline ml-1 text-[#5B8DEF]" title="Recurring series" /> : null}</td>
                <td>{inv.client_name}</td>
                <td className="mono text-xs">{inv.issue_date}</td>
                <td className="num">{SYM[inv.currency] || ""}{money(inv.total)} <span className="text-white/40 text-xs">{inv.currency}</span></td>
                <td className="num text-white/70">{inv.total_base != null ? `\u20ac${money(inv.total_base)}` : "—"}</td>
                <td>
                  <select value={inv.status} onChange={(e) => setStatus(inv.invoice_id, e.target.value)} data-testid={`invoice-status-${inv.invoice_id}`}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${STATUS_STYLE[inv.status] || ""}`} style={{ width: "auto" }}>
                    {["draft", "sent", "paid", "void"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="flex gap-2 py-2">
                  {inv.status !== "paid" && inv.status !== "void" && inv.pay_token && (
                    <a href={`${BACKEND}/api/invoices/pay/${inv.pay_token}`} target="_blank" rel="noreferrer" className="text-white/50 hover:text-[#00A86B]" data-testid={`invoice-pay-${inv.invoice_id}`} title="Pay now (Stripe)"><CreditCard size={16} /></a>
                  )}
                  <button onClick={() => setEmailFor(inv)} className="text-white/50 hover:text-[#5B8DEF]" data-testid={`invoice-email-${inv.invoice_id}`} title="Email to client"><EnvelopeSimple size={16} /></button>
                  <button onClick={() => downloadPdf(inv)} className="text-white/50 hover:text-[#FFB020]" data-testid={`invoice-pdf-${inv.invoice_id}`} title="Download PDF"><FileArrowDown size={16} /></button>
                  {!inv.recurring_id && <button onClick={() => makeRecurring(inv)} className="text-white/50 hover:text-[#00A86B]" data-testid={`invoice-recur-${inv.invoice_id}`} title="Make monthly recurring"><ArrowsClockwise size={16} /></button>}
                  <button onClick={() => del(inv.invoice_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`invoice-del-${inv.invoice_id}`}><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {activeRec.length > 0 && (
        <div className="glass rounded-xl p-6" data-testid="recurring-section">
          <div className="flex items-center gap-2 eyebrow mb-4"><ArrowsClockwise size={14} weight="duotone" color="#5B8DEF" /> Recurring invoices</div>
          <div className="space-y-2">
            {activeRec.map((r) => (
              <div key={r.recurring_id} className="flex items-center justify-between rounded-lg bg-black/25 border border-white/5 p-3" data-testid={`recurring-${r.recurring_id}`}>
                <div className="text-sm">
                  <span className="text-white/85">{r.template?.client_name}</span>
                  <span className="text-white/45 text-xs ml-2">{SYM[r.template?.currency] || ""}{money((r.template?.line_items || []).reduce((s, l) => s + l.qty * l.unit_price, 0))} {r.template?.currency} · monthly</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-white/55">Next: <span className="mono">{r.next_run}</span> · {r.generated_count} raised</span>
                  <button onClick={() => stopRecurring(r.recurring_id)} data-testid={`recurring-stop-${r.recurring_id}`} className="text-white/50 hover:text-[#E63946] flex items-center gap-1 text-xs"><Stop size={14} weight="fill" /> Stop</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {open && <InvoiceModal directory={directory} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {emailFor && <EmailModal inv={emailFor} onClose={() => setEmailFor(null)} onSent={() => { setEmailFor(null); load(); }} />}
      {showClients && <ClientsModal directory={directory} onClose={() => setShowClients(false)} onChanged={load} />}
    </div>
  );
}

function ClientsModal({ directory, onClose, onChanged }) {
  const empty = { name: "", email: "", address: "", us_state: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null); // client_id being edited
  const [saving, setSaving] = useState(false);
  const [usStates, setUsStates] = useState([]);
  const items = directory?.items || [];

  useEffect(() => { api.get("/compliance/us/states").then((r) => setUsStates(r.data.states || [])).catch(() => {}); }, []);

  const startEdit = (c) => { setEditing(c.client_id); setForm({ name: c.name || "", email: c.email || "", address: c.address || "", us_state: c.us_state || "" }); };
  const reset = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.email.trim()) { toast.error("A name or email is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client saved");
      reset(); onChanged();
    } catch (err) { notifyError(err); } finally { setSaving(false); }
  };
  const del = async (c) => {
    if (!window.confirm(`Remove ${c.name || c.email} from your client directory?`)) return;
    try { await api.delete(`/clients/${c.client_id}`); if (editing === c.client_id) reset(); onChanged(); }
    catch (err) { notifyError(err); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl p-7 w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="clients-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Users size={20} className="text-[#5B8DEF]" /> <span className="font-display text-2xl">Client directory</span></div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-sm text-white/55 mb-4">Save clients once — their details autofill when you raise a new invoice. Clients are also added automatically the first time you invoice them.</p>

        <form onSubmit={save} className="rounded-xl bg-black/25 border border-white/5 p-4 space-y-3 mb-5">
          <div className="eyebrow">{editing ? "Edit client" : "Add client"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label>Name</label><input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Ohio LLC" /></div>
            <div><label>Email</label><input type="email" data-testid="client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="client@company.com" /></div>
          </div>
          <div><label>Address (optional)</label><textarea rows="2" data-testid="client-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><label>US state (optional — powers the nexus sales tracker)</label>
            <select data-testid="client-us-state" value={form.us_state} onChange={(e) => setForm({ ...form, us_state: e.target.value })}>
              <option value="">— Not a US client —</option>
              {usStates.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            {editing && <button type="button" onClick={reset} className="btn-ghost">Cancel edit</button>}
            <button type="submit" disabled={saving} data-testid="client-save" className="btn-gold">{saving ? "Saving…" : editing ? "Update client" : "Add client"}</button>
          </div>
        </form>

        <div className="space-y-2">
          {items.length === 0 ? (
            <div className="text-center text-white/40 py-6" data-testid="clients-empty">No saved clients yet. Add one above.</div>
          ) : items.map((c) => (
            <div key={c.client_id} className="flex items-center justify-between rounded-lg bg-black/25 border border-white/5 p-3" data-testid={`dir-client-${c.client_id}`}>
              <div className="text-sm min-w-0">
                <div className="text-white/85 truncate">{c.name || "—"}</div>
                <div className="text-white/40 text-xs truncate">{c.email || "no email"}{c.address ? ` · ${c.address}` : ""}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => startEdit(c)} data-testid={`client-edit-${c.client_id}`} className="btn-ghost text-xs">Edit</button>
                <button onClick={() => del(c)} data-testid={`client-del-${c.client_id}`} className="text-white/40 hover:text-[#E63946]"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function EmailModal({ inv, onClose, onSent }) {
  const [to, setTo] = useState(inv.client_email || "");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try { const r = await api.post(`/invoices/${inv.invoice_id}/email`, { to, note }); toast.success(`Invoice sent to ${r.data.to}`); onSent(); }
    catch (err) { notifyError(err); } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl p-7 w-full max-w-md" data-testid="invoice-email-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><EnvelopeSimple size={20} className="text-[#5B8DEF]" /> <span className="font-display text-xl">Email {inv.number}</span></div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-sm text-white/55 mb-4">Sends a branded PDF of this invoice. A draft is automatically marked as sent.</p>
        <form onSubmit={send} className="space-y-4">
          <div><label>Recipient email</label><input required type="email" data-testid="email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@company.com" /></div>
          <div><label>Message (optional)</label><textarea rows="3" data-testid="email-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please find attached this month's invoice." /></div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={sending} data-testid="email-send" className="btn-gold"><EnvelopeSimple size={14} weight="fill" /> {sending ? "Sending…" : "Send invoice"}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

const Tile = ({ label, value, warn }) => (
  <div className={`glass rounded-xl p-5 ${warn ? "ring-1 ring-[#FFB020]/40" : ""}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
    <div className={`mono text-2xl mt-2 ${warn ? "text-[#FFB020]" : ""}`}>{value}</div>
  </div>
);

function InvoiceAnalytics({ a, aging }) {
  const chart = (a.revenue_by_month || []).map((m) => ({ label: m.month.slice(2), revenue: m.revenue }));
  const maxRev = Math.max(1, ...chart.map((c) => c.revenue));
  const AGE_COLOR = { current: "#00A86B", "1_30": "#8FD14F", "31_60": "#FFB020", "61_90": "#FF7A45", "90_plus": "#E63946" };
  return (
    <div className="glass rounded-xl p-6" data-testid="invoice-analytics">
      <div className="flex items-center gap-2 eyebrow mb-4"><ChartBar size={14} weight="duotone" color="#FFB020" /> Invoice analytics</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl bg-black/25 border border-white/5 p-4" data-testid="an-paid">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Paid (EUR)</div>
          <div className="mono text-xl mt-2 text-[#00A86B]">{"\u20ac"}{money(a.paid.base_total)}</div>
          <div className="text-xs text-white/40 mt-1">{a.paid.count} invoice{a.paid.count === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-xl bg-black/25 border border-white/5 p-4" data-testid="an-outstanding">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Outstanding (EUR)</div>
          <div className="mono text-xl mt-2 text-[#FFB020]">{"\u20ac"}{money(a.outstanding.base_total)}</div>
          <div className="text-xs text-white/40 mt-1">{a.outstanding.count} open</div>
        </div>
        <div className="rounded-xl bg-black/25 border border-white/5 p-4" data-testid="an-dtp">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Avg days to pay</div>
          <div className="mono text-xl mt-2">{a.avg_days_to_pay != null ? `${a.avg_days_to_pay}d` : "—"}</div>
        </div>
        <div className="rounded-xl bg-black/25 border border-white/5 p-4" data-testid="an-collection">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Collection rate</div>
          <div className="mono text-xl mt-2">{a.collection_rate != null ? `${a.collection_rate}%` : "—"}</div>
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Revenue by month (EUR, paid)</div>
      <div style={{ width: "100%", height: 160 }} data-testid="an-revenue-chart">
        <ResponsiveContainer>
          <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: "#8A9A94", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#0A1311", border: "1px solid #1B2624", borderRadius: 8, fontSize: 12 }} formatter={(v) => [`\u20ac${money(v)}`, "Revenue"]} />
            <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
              {chart.map((c, i) => <Cell key={i} fill={c.revenue >= maxRev ? "#FFB020" : "#00A86B"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {aging && aging.total_outstanding_base > 0 && (
        <div className="mt-6 pt-6 border-t border-white/5" data-testid="invoice-aging">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Accounts-receivable aging (EUR outstanding)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {aging.buckets.map((b) => (
              <div key={b.key} className="rounded-xl bg-black/25 border border-white/5 p-3" data-testid={`aging-${b.key}`}>
                <div className="flex items-center gap-1.5 text-[11px] text-white/60"><span className="w-1.5 h-1.5 rounded-full" style={{ background: AGE_COLOR[b.key] }} />{b.label}</div>
                <div className="mono text-lg mt-1.5" style={{ color: b.base_total > 0 ? AGE_COLOR[b.key] : "#6B7770" }}>{"\u20ac"}{money(b.base_total)}</div>
                <div className="text-[10px] text-white/35">{b.count} invoice{b.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>
          {(aging.slow_payers || []).length > 0 && (
            <div className="mt-3 text-xs text-white/55" data-testid="aging-slow">
              Slowest: {aging.slow_payers.slice(0, 3).map((s) => `${s.client_name} (${s.max_days_overdue}d)`).join(", ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InvoiceModal({ directory, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_address: "", issue_date: today, due_date: "", currency: "EUR", tax_rate: "0", notes: "", status: "draft" });
  const [lines, setLines] = useState(() => [newInvLine()]);
  const [saving, setSaving] = useState(false);
  const savedClients = directory?.items || [];

  const pickClient = (id) => {
    const c = savedClients.find((x) => x.client_id === id);
    if (!c) return;
    setForm((f) => ({ ...f, client_name: c.name || "", client_email: c.email || "", client_address: c.address || "" }));
  };

  const sym = SYM[form.currency] || "";
  const subtotal = lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.unit_price || 0)), 0);
  const tax = subtotal * (Number(form.tax_rate || 0) / 100);
  const total = subtotal + tax;

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines((ls) => [...ls, newInvLine()]);
  const rmLine = (i) => setLines((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/invoices", {
        ...form,
        tax_rate: Number(form.tax_rate || 0),
        line_items: lines.filter((l) => l.description.trim()).map((l) => ({ description: l.description, qty: Number(l.qty || 0), unit_price: Number(l.unit_price || 0) })),
      });
      onSaved();
    } catch (err) { notifyError(err); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl p-7 w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="invoice-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><FileText size={20} className="text-[#FFB020]" /> <span className="font-display text-2xl">New invoice</span></div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {savedClients.length > 0 && (
            <div><label>Autofill from saved client</label>
              <select data-testid="inv-client-picker" defaultValue="" onChange={(e) => pickClient(e.target.value)}>
                <option value="">— Select a saved client —</option>
                {savedClients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name || c.email}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label>Client name</label><input required data-testid="inv-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="e.g. Acme Ohio LLC" /></div>
            <div><label>Currency</label><select data-testid="inv-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.sym})</option>)}</select></div>
          </div>
          <div><label>Client address (optional)</label><textarea data-testid="inv-address" rows="2" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} /></div>
          <div><label>Client email (optional — enables emailing)</label><input type="email" data-testid="inv-client-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} placeholder="client@company.com" /></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label>Issue date</label><input required type="date" data-testid="inv-issue" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
            <div><label>Due date</label><input type="date" data-testid="inv-due" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            <div><label>Tax rate (%)</label>
              <input type="number" step="0.1" data-testid="inv-tax" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
              <div className="flex gap-1.5 mt-1.5" data-testid="inv-vat-presets">
                {[["23", "Standard"], ["13.5", "Reduced"], ["0", "Zero"]].map(([rate, label]) => (
                  <button key={rate} type="button" data-testid={`inv-vat-preset-${rate}`} onClick={() => setForm({ ...form, tax_rate: rate })}
                    className={`text-[11px] px-2 py-1 rounded-lg border transition ${String(form.tax_rate) === rate ? "border-[#FFB020] text-[#FFB020] bg-[#FFB020]/10" : "border-white/12 text-white/55 hover:border-white/25"}`}>
                    {rate}% <span className="text-white/40">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-black/25 border border-white/5 p-4">
            <div className="eyebrow mb-2">Line items</div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={l._id} className="grid grid-cols-12 gap-2 items-center">
                  <input className="col-span-6" placeholder="Description" data-testid={`inv-line-desc-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} />
                  <input className="col-span-2" type="number" step="0.01" placeholder="Qty" data-testid={`inv-line-qty-${i}`} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                  <input className="col-span-3" type="number" step="0.01" placeholder={`Unit (${sym})`} data-testid={`inv-line-price-${i}`} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                  <button type="button" onClick={() => rmLine(i)} className="col-span-1 text-white/40 hover:text-[#E63946]"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addLine} data-testid="inv-add-line" className="btn-ghost text-xs mt-3"><Plus size={14} /> Add line</button>
          </div>

          <div className="flex justify-end">
            <div className="text-sm space-y-1 text-right">
              <div className="text-white/60">Subtotal: <span className="mono text-white">{sym}{money(subtotal)}</span></div>
              <div className="text-white/60">Tax ({Number(form.tax_rate || 0)}%): <span className="mono text-white">{sym}{money(tax)}</span></div>
              <div className="text-lg">Total: <span className="mono text-[#FFB020]" data-testid="inv-total">{sym}{money(total)}</span></div>
            </div>
          </div>

          <div><label>Notes (optional)</label><input data-testid="inv-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving} data-testid="inv-save" className="btn-gold">{saving ? "Saving…" : "Create invoice"}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
